import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import ContainerDimensions from 'react-container-dimensions';
import { connect } from 'react-redux';
import { Row, Col, Glyphicon, InputGroup } from 'react-bootstrap';
import Moment from 'moment';
import { browserHistory } from 'react-router';
import $ from 'jquery';
import javascriptTimeAgo from 'javascript-time-ago';
import Button from './Button';
import CheckBox from './CheckBox';
import Select from './Select';
import TaskEditorField from './TaskEditorField';
import { caseSetServerError } from '../../actions/caseActions';


// Load locale specific relative date/time messages
//
import { colorMap, requiredFormFieldColor } from '../../helpers/colors';
import { updateTask, dateSort, filterTasks, findBootstrapEnvironment, getAssignedToFullName, isDateOlderThan, getDateTimeUTC } from './../../helpers/utils';
import ReactTablePagination from './ReactTablePagination';
import { fitToViewport } from '../../helpers/fitHeight';


import enLocale from 'javascript-time-ago/locales/en';
import 'javascript-time-ago/intl-messageformat-global';
import 'intl-messageformat/dist/locale-data/en';
javascriptTimeAgo.locale(enLocale);

class TaskTable extends PureComponent {
    constructor(props) {
        super(props);
        this.filterMethod = this.filterMethod.bind(this);
        this.storageKey = this.storageKey.bind(this);
        this.persistFilter = this.persistFilter.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.fitHeight = this.fitHeight.bind(this);
        this.state = {
            // Rows rendered so far; more are added while the list is scrolled (see onScroll).
            visibleRows: 20,
            loading: false,
            dialogOpen: false,
            taskEditField: {},
            filterValue: this.getPersistedFilter(props) || (props.allTasks ? 'false' : 'all_blocked'),
        };

        this.columns = [];
    }

    componentWillReceiveProps(nextProps) {
        // On a fresh reload the user may not be in the store yet when the
        // constructor reads the persisted filter (so it falls back to the
        // 'anon' key). Once the user arrives, re-read under the correct key
        // and apply it to both the dropdown and the table.
        //
        const prevId = this.props.loggedIn && this.props.loggedIn.id;
        const nextId = nextProps.loggedIn && nextProps.loggedIn.id;
        if (nextId && nextId !== prevId) {
            const persisted = this.getPersistedFilter(nextProps);
            if (persisted && persisted !== this.state.filterValue) {
                this.setState({ filterValue: persisted });
                if (this.refs['taskTable']) {
                    this.refs['taskTable'].filterColumn(
                        { Header: '', accessor: 'Completed', id: 'Completed' },
                        persisted);
                }
            }
        }
    }

    // Per-user, per-context (home vs. case) storage key for the remembered filter.
    //
    storageKey(props = this.props) {
        const userId = (props.loggedIn && props.loggedIn.id) || 'anon';
        return `taskFilter:${props.allTasks ? 'home' : 'case'}:${userId}`;
    }

    getPersistedFilter(props) {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                return window.localStorage.getItem(this.storageKey(props));
            }
        } catch (e) { /* localStorage unavailable */ }

        return null;
    }

    persistFilter(value) {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                window.localStorage.setItem(this.storageKey(), value);
            }
        } catch (e) { /* localStorage unavailable */ }
    }

    filterMethod(filter, row) {
        const task = row._original;
        const notBlockedBy = () => {
            if (task['BlockedBy']) {
                const blockedBy = this.props.taskIdsMap[parseInt(task['BlockedBy'], 10)];
                if (blockedBy) {
                    return blockedBy['Completed'];
                }
            }

            return true;
        };

        if (filter.value === "due") {
            const hasDate = task['DateTimeStart'] || task['DateTimeEnd'];
            return !!hasDate && !task['Completed'] && notBlockedBy();
        }

        if (filter.value === "all_blocked") {
            return notBlockedBy();
        }

        if (filter.value === "all") {
            return true;
        }

        return !task['Completed'] && notBlockedBy();
    }

    fitHeight() {
        fitToViewport(this.refs.taskTable);
    }

    componentDidMount() {
        window.addEventListener('resize', this.fitHeight);
        this.fitHeight();
    }

    componentDidUpdate() {
        this.fitHeight();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.fitHeight);
    }

    onScroll(e) {
        const el = e.currentTarget;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
            this.setState(({ visibleRows }) => ({ visibleRows: visibleRows + 20 }));
        }
    }

    render() {
        const {
            width,
            height,
            currentCase,
            currentConfig,
            tasks,
            loggedIn,
            loading,
        } = this.props;

        const getDate = (task) => {
            if (!task) {
                return undefined;
            }
            
            let date;
            if (task['DateTimeStart']) {
                date = new Date(task['DateTimeStart']);
            } else if (task['DateTimeEnd']) {
                date = new Date(task['DateTimeEnd']);
            }

            return date;
        };

        this.columns = [
            {
                Header: '',
                accessor: 'Completed',
                width: 36,
                className: 'ct-narrow',
                headerClassName: 'ct-narrow',
                Cell: (row) => { return (
                    <div style={{textAlign: 'center'}}>
                    <CheckBox field={{
                                 name: 'Completed',
                              }}
                              checked={row.original['Completed']}
                              disabled={this.state.loading || row.original['ActionTrigger'] === 'due_datetime'}
                              onChange={() => {
                                  const task = { ...row.original };
                                  task['Completed'] = !task['Completed'];
                                  if (task['Completed']) {
                                      delete task['ActionError'];
                                      delete task['ActionPerformed'];
                                  }
                                  task['$Modified$'] = true;
                                  this.setState({ loading: true });
                                  updateTask(task, this.props, () => this.setState({ loading: false }), (error) => {
                                      console.error(new Error().stack, error);
                                      this.props.dispatch(caseSetServerError(error));
                                      this.setState({ loading: false });
                                  });
                              }}
                    />
                    </div>
                    );
                },
            },
            {
                Header: 'Date',
                id: 'Date',
                accessor: getDate,
                width: 130,
                sortMethod: dateSort,
                Cell: (row) => {
                    const task = row.original;
                    const date = getDate(task);
                    let dateStr = '';
                    const format = process.env.DATE_FORMAT + ' HH:mm';
                    if (date) {
                        dateStr = new Moment(date).format(format);
                    }

                    if (task['DateTimeStart'] && task['DeadlineType'].showDateStart &&
                        task['DateTimeEnd'] && task['DeadlineType'].showDateEnd) {

                        dateStr = new Moment(new Date(task['DateTimeStart'])).format(format);
                        dateStr += ' - ';
                        dateStr += new Moment(new Date(task['DateTimeEnd'])).format('HH:mm');
                    }
                    
                    let component = dateStr;
                    if (dateStr && task['DateTimeEnd'] && loggedIn && !isDateOlderThan(getDateTimeUTC(task['DateTimeEnd'], loggedIn && loggedIn['Timezone']), '1', 'minutes')) {
                        component = (<div style={{ color: requiredFormFieldColor }}>{dateStr}</div>);
                    } else if (task['Completed']) {
                        component = (<div style={{ textDecoration: 'line-through' }}>{dateStr}</div>);
                    }

                    return component;
                }
            },
            {
                Header: 'Title',
                accessor: 'Title',
                minWidth: 220,
                Cell: (row) => {
                    let task = row.original;
                    let component = row.value;
                    // if (task['ActionType'] === 'set_case_state') {
                    //     component = (<div style={{ textDecoration: task['Completed'] && 'line-through' }}><Glyphicon glyph="transfer" style={{ fontSize: 15 }} /> {component}</div>);
                    // } else if (task['ActionType'] === 'send_email') {
                    //     component = (<div style={{ textDecoration: task['Completed'] && 'line-through' }}><Glyphicon glyph="envelope" style={{ fontSize: 15 }} /> {component}</div>);
                    // }
                    
                    if (task['Completed']) {
                        component = (<div style={{ textDecoration: 'line-through' }}>{component}</div>);
                    }

                    return component;
                }
            },
            {
                Header: 'Assigned To',
                accessor: 'AssignedTo',
                width: 110,
                Cell: row => {
                    return getAssignedToFullName(row.value, this.props.loggedIn, this.props.employees);
                }
            }
        ];

        // No task has a date: the column would be empty, so drop it and give the room to the title.
        if (tasks && tasks.length > 0 && !tasks.some(getDate)) {
            this.columns = this.columns.filter(c => c.id !== 'Date');
        }

        let defaultFilter;
        if (this.props.allTasks && !this.props.onClickViewCase) {
            this.columns.push({
                Header: '',
                accessor: 'CaseId',
                width: 36,
                className: 'ct-narrow',
                headerClassName: 'ct-narrow',
                Cell: (row) => { 
                    if (!row.original['CaseId']) {
                        return <div/>;
                    }

                    return (
                    <div style={{textAlign: 'center'}}>
                    <Button field={{
                                 name: 'CaseId',
                            }}
                            style={{padding: 0}}
                            disabled={this.state.loading}
                            icon='share-alt'
                            bsStyle="link"
                            onClick={() => {
                                let task = row.original;
                                browserHistory.push(`/case/edit/intake/${task['CaseId']}`);
                            }}
                    />
                    </div>
                    )},
            });

        }

        defaultFilter = [
            {
                id: 'Completed',
                value: this.state.filterValue
            }
        ];

        if (!tasks || loading) {
            return (<div style={{textAlign: 'center'}}>Loading ...</div>);
        }

        const size = findBootstrapEnvironment();
        return (
            <div>
            <Row style={{ marginBottom: '10px' }}>
                <Col xs={12} md={9} id="tour-tasks-filter">
                    <InputGroup>
                    <InputGroup.Addon>
                        <Glyphicon glyph="filter" />
                    </InputGroup.Addon>
                    <Select field={{
                                name: 'filter',
                                value: this.state.filterValue,
                                onChange: (evt) => {
                                    const value = evt.target.value;
                                    this.setState({filterValue: value});
                                    this.persistFilter(value);
                                    this.refs['taskTable'].filterColumn(
                                        {Header: '', accessor: 'Completed', id: 'Completed'},
                                        value);

                                }
                            }}
                            name="filter"
                            id="tour-case-tasks-filter"
                            embedded
                            options={[
                                {label: 'Due / Late only', value: 'due'},
                                {label: 'Incomplete', value: 'false'},
                                {label: 'All (Except Blocked)', value: 'all_blocked'},
                                {label: 'All', value: 'all'},
                            ]}
                    />
                    </InputGroup>
                </Col>
            </Row>
            <Row>
            <Col xs={12} id="tour-tasks">
            <ReactTable
                PaginationComponent={ReactTablePagination}
                ref="taskTable"
                data={tasks}
                className="-highlight ct-scroll"
                defaultFilterMethod={this.filterMethod}
                defaultFiltered={defaultFilter}
                showPagination={false}
                pageSize={Math.max(Math.min(this.state.visibleRows, tasks ? tasks.length : 0), 1)}
                getTbodyProps={() => ({ onScroll: this.onScroll })}
                minRows={0}
                sortable
                multiSort
                resizable
                filterable={false}
                noDataText=''
                columns={this.columns}
                loading={tasks === undefined}
                style={{
                    width: width - 30,
                    maxHeight: height,
                }}
                getTableProps={() => {
                    return {
                        style: {
                            borderLeft: "0 none",
                            borderRight: "0 none",
                        }
                    }
                }}
                getTrProps={(state, rowInfo, column) => {
                    let task = rowInfo.original;
                    let background = '';
                    if (task) {
                        if (task['Completed']) {
                            background = 'rgba(63,144,63,0.15)';
                        }

                        if (task['ActionError']) {
                            background = `rgba(${colorMap['red']}, 0.15)`;
                        }
                    }

                    return {
                        style: {
                            paddingTop: 5,
                            height: 45,
                            cursor: task && task['CaseId'] ? 'pointer' : 'auto',
                            background,
                            fontWeight: task['Title'] && task['Title'].indexOf('!') >= 0 ? 'bold' : undefined
                        }
                    }
                }}
                getTheadThProps={(state, rowInfo, column) => {
                    return {
                        style: {
                            fontWeight: 'bold',
                            paddingTop: 20,
                            paddingBottom: 5,
                            textAlign: 'left',
                        }
                    }
                }}
                getTdProps={(state, rowInfo, column, instance) => {
                    return {
                        onClick: (e, handleOriginal) => {
                            if (column.id === 'Completed') {
                                return;
                            }

                            if (!this.state.loading) {
                                if (this.props.onClickViewCase) {
                                    let task = rowInfo.original;
                                    if (task['CaseId']) {
                                        $('html, body').animate({
                                            scrollTop: $("#mainNavBar").offset().top
                                        }, 0);

                                        browserHistory.push(`/case/edit/intake/${task['CaseId']}`);                                    
                                    }
                                } else {                                        
                                    this.setState({
                                        taskEditField: rowInfo.original,
                                        dialogOpen: true,
                                    });
                                }
                            }

                            if (handleOriginal) {
                                handleOriginal()
                            }
                        }
                    }
                  }}
            />
            </Col>
            </Row>
            <TaskEditorField initialField={this.state.taskEditField}
                             showAsDialog
                             onChange={(taskEditField) => {this.setState({ taskEditField });}}
                             dialogOpen={this.state.dialogOpen}
                             dialogCancel={() => this.setState({ dialogOpen: false })}
                             dialogConfirm={() => this.setState({ dialogOpen: false })}
                             tasks={tasks}
                             disableMove
                             disableCheckBox={false}
                             editMode
                             renderGoToCaseButton={false}
                             isEmptyChronoError={false}
                             caseType={currentCase && currentCase['CaseType']}
                             currentCase={currentCase} />
            </div>
        );
    }
}

class _TaskTable extends PureComponent {
    render() {
        return (
            <ContainerDimensions>
                <TaskTable {...this.props}/>
            </ContainerDimensions>);
    }
}

TaskTable.propTypes = {
    width: PropTypes.int,
    height: PropTypes.int,
    allTasks: PropTypes.bool,
    allTasksAllowedCaseIds: PropTypes.array,
};

const mapStateToProps = (state, ownProps) => {
    let tasks = state.user.tasks;
    const currentCase = state.cases.current;
    const cases = state.cases.rows;
    if (ownProps.allTasks) {
        tasks = filterTasks(tasks, ownProps.allTasksAllowedCaseIds, false);
    } else {
        if (currentCase && currentCase.id) {
            tasks = filterTasks(tasks, currentCase && currentCase.id, false);
        } else {
            tasks = [];
        }
    }

    const taskIdsMap = {};
    (tasks || []).forEach(task => {
        taskIdsMap[task.id] = task;
    })

    return {
        currentConfig: state.user.config,
        currentCase,
        cases,
        tasks,
        taskIdsMap,
        loggedIn: state.user.loggedIn,
        employees: state.user.employees,
    };
};

export default connect(mapStateToProps)(_TaskTable);